import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Videocam as CamOnIcon,
  VideocamOff as CamOffIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { getCameraSocketUrl } from '../../utils/cameraSocketUrl';
import { getCameraIoOptions } from '../../utils/cameraIoOptions';
import { useAuth } from '../../context/AuthContext';

/*
 * Deployment note (Docker Desktop K8s, GKE, EKS, etc.):
 * The webcam is always opened on the USER'S machine via the browser (getUserMedia)—never inside a pod.
 * Video frames are encoded in the browser and sent over Socket.IO to whichever cluster hosts the API.
 * Pods only receive the stream; they do not access the user's physical camera. Requirements: HTTPS
 * (or localhost) for getUserMedia, and ingress must route /api and the Socket.IO path to the backend.
 */

const FRAME_MS = 280;
const SOCKET_WAIT_MS = 25000;

/** Wait until Socket.IO connects or timeout (for use after user already granted camera). */
function waitForSocketConnection(socket, timeoutMs) {
  return new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error('Connection not ready. Try Refresh connection or reload the page.'));
      return;
    }
    if (socket.connected) {
      resolve();
      return;
    }
    let settled = false;
    const cleanup = () => {
      clearTimeout(timer);
      socket.off('connect', onDone);
      socket.off('reconnect', onDone);
    };
    const onDone = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(
        new Error(
          'Could not reach the live streaming server in time. Check your network, VPN, or API/WebSocket settings, then use “Refresh connection”.'
        )
      );
    }, timeoutMs);
    socket.on('connect', onDone);
    socket.on('reconnect', onDone);
    try {
      socket.connect();
    } catch {
      /* io may already be connecting */
    }
  });
}

const PERMISSION_DISCLAIMER = (
  <>
    <Typography variant="body2" paragraph sx={{ fontWeight: 600 }}>
      This app will ask to use your device camera so live video can be sent to authorized people in your
      organization (for example, managers using Camera monitor).
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
      What you are agreeing to
    </Typography>
    <Typography variant="body2" component="div" paragraph>
      If you choose <strong>Allow</strong> below, you voluntarily consent to:
    </Typography>
    <Box component="ul" sx={{ pl: 2, my: 0 }}>
      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
        Starting a video stream from your camera to the organization&apos;s systems for the duration you leave
        streaming on (subject to your employer&apos;s policies).
      </Typography>
      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
        Possible recording, retention, or monitoring of that video for training, quality, security, or work
        oversight, as allowed by your organization and applicable law.
      </Typography>
      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
        Your responsibility for professional appearance and conduct on camera, and for what appears in the
        frame; the software vendor is not responsible for how you choose to present yourself or your
        environment.
      </Typography>
    </Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
      Your choice and the browser prompt
    </Typography>
    <Typography variant="body2" paragraph>
      After you click <strong>Allow</strong>, we connect to the live server first, then your browser will ask
      to use the camera—you can allow or deny there as well. If you deny either step, no stream is sent.
    </Typography>
    <Typography variant="body2" paragraph>
      You can stop streaming at any time with <strong>Stop</strong>, which withdraws this session&apos;s
      stream. This screen is not a substitute for your employment contract, handbook, or legal advice—contact
      HR or legal if you have questions.
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Choose <strong>Deny</strong> to close without turning on the camera.
    </Typography>
  </>
);

const EmployeeCamera = () => {
  const { isAdmin } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const socketRef = useRef(null);
  const mediaStreamRef = useRef(null);

  const [socketReady, setSocketReady] = useState(false);
  const [socketEpoch, setSocketEpoch] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(true);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [connectingServer, setConnectingServer] = useState(false);

  const postEvent = async (event_type) => {
    await api.post('/camera/events', { event_type });
  };

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const r = await api.get('/camera/my-summary');
      setSummary(r.data?.data || null);
    } catch {
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/camera/eligibility', { skipErrorToast: true });
        if (!cancelled) setEligibility(r.data?.data ?? null);
      } catch {
        if (!cancelled) setEligibility({ eligible: false, message: 'Could not verify camera eligibility.' });
      } finally {
        if (!cancelled) setLoadingEligibility(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const token = getAuthAccessToken();
    if (!token) return undefined;
    const url = getCameraSocketUrl();
    const s = io(url, getCameraIoOptions(token));
    socketRef.current = s;
    let connectErrToastShown = false;
    const onConnect = () => {
      setSocketReady(true);
      connectErrToastShown = false;
    };
    s.on('connect', onConnect);
    s.on('disconnect', () => setSocketReady(false));
    s.on('connect_error', (err) => {
      setSocketReady(false);
      console.error('[camera socket] connect_error', err?.message || err, {
        url,
        path: s.io?.opts?.path,
      });
      if (!connectErrToastShown) {
        connectErrToastShown = true;
        toast.error(
          'Live streaming server is not reachable. It must use the same host as your API (see REACT_APP_API_URL at build time, or port-forward). Open the browser console (F12) for details. Try “Refresh connection”.'
        );
      }
    });
    return () => {
      s.off('connect', onConnect);
      s.disconnect();
      socketRef.current = null;
      setSocketReady(false);
    };
  }, [socketEpoch]);

  const refreshSocketConnection = useCallback(() => {
    const s = socketRef.current;
    if (s) {
      try {
        s.disconnect();
      } catch {
        /* ignore */
      }
    }
    setSocketReady(false);
    setSocketEpoch((e) => e + 1);
  }, []);

  const stopMedia = () => {
    const st = mediaStreamRef.current;
    if (st) {
      st.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const waitPublisherOk = () =>
    new Promise((resolve, reject) => {
      const sock = socketRef.current;
      if (!sock) {
        reject(new Error('Not connected'));
        return;
      }
      const t = setTimeout(() => {
        cleanup();
        reject(new Error('Camera registration timed out'));
      }, 12000);
      const cleanup = () => {
        clearTimeout(t);
        sock.off('camera:publisher-ok', onOk);
        sock.off('camera:error', onErr);
      };
      const onOk = () => {
        cleanup();
        resolve();
      };
      const onErr = (payload) => {
        cleanup();
        reject(new Error(payload?.message || 'Camera registration refused'));
      };
      sock.once('camera:publisher-ok', onOk);
      sock.once('camera:error', onErr);
      sock.emit('camera:publisher-register');
    });

  /**
   * After user clicks Allow: connect live server first (no camera yet), then browser camera, then register.
   * Avoids turning the camera on if the server never connects; avoids facingMode constraints that break desktops.
   */
  const runAllowStreaming = async () => {
    setStarting(true);
    setConnectingServer(true);
    try {
      try {
        await waitForSocketConnection(socketRef.current, SOCKET_WAIT_MS);
      } catch (err) {
        toast.error(err.message || 'Server connection failed');
        return;
      } finally {
        setConnectingServer(false);
      }

      if (!socketRef.current?.connected) {
        toast.error('Live server disconnected. Use Refresh connection and try again.');
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error(
          'Camera is not available in this context. Use https:// (or http://localhost) and a current browser.'
        );
        return;
      }

      let stream;
      const tryVideo = async () => {
        try {
          return await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 640, max: 1920 },
              height: { ideal: 480, max: 1080 },
            },
            audio: false,
          });
        } catch (first) {
          if (
            first?.name === 'OverconstrainedError' ||
            first?.name === 'ConstraintNotSatisfiedError'
          ) {
            return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          }
          throw first;
        }
      };
      try {
        stream = await tryVideo();
      } catch (e) {
        const name = e?.name;
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          toast.info('Camera access was blocked. Allow the camera in the browser prompt or site settings.');
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          toast.error('No camera was found. Plug in a webcam or enable a built-in camera.');
        } else if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
          toast.error('This camera could not be opened with any supported settings.');
        } else {
          toast.error(e?.message || 'Could not access the camera');
        }
        return;
      }

      mediaStreamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        try {
          await v.play();
        } catch {
          v.muted = true;
          try {
            await v.play();
          } catch {
            /* preview may still update for streaming */
          }
        }
      }

      try {
        await postEvent('stream_start');
      } catch (err) {
        stopMedia();
        const msg = err.response?.data?.message || err.message || 'Could not start camera session';
        toast.error(msg);
        return;
      }
      try {
        await waitPublisherOk();
      } catch (err) {
        try {
          await postEvent('stream_stop');
        } catch {
          /* ignore */
        }
        stopMedia();
        toast.error(err.message || 'Could not register camera with server');
        return;
      }
      setPublishing(true);
      setPaused(false);
      toast.success('Video streaming is on');
    } catch (e) {
      stopMedia();
      toast.error(e.message || 'Something went wrong');
    } finally {
      setStarting(false);
      setConnectingServer(false);
      setPermissionOpen(false);
    }
  };

  const openPermissionDialog = () => {
    setPermissionOpen(true);
  };

  const stopStream = async () => {
    try {
      await postEvent('stream_stop');
    } catch {
      /* still stop locally */
    }
    socketRef.current?.emit('camera:publisher-unregister');
    stopMedia();
    setPublishing(false);
    setPaused(false);
    toast.info('Camera stopped');
    loadSummary();
  };

  const doPause = async () => {
    try {
      await postEvent('pause');
      setPaused(true);
      toast.info('Feed paused (admins see last frame until you resume)');
    } catch {
      toast.error('Failed to record pause');
    }
  };

  const doResume = async () => {
    try {
      await postEvent('resume');
      setPaused(false);
      toast.success('Feed resumed');
    } catch {
      toast.error('Failed to record resume');
    }
  };

  useEffect(() => {
    if (!publishing || paused || !socketReady) return undefined;
    const socket = socketRef.current;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!socket || !video || !canvas) return undefined;

    const tick = () => {
      if (!socket.connected || paused || !publishing) return;
      if (video.readyState < 2) return;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return;
      const w = 426;
      const h = Math.max(1, Math.round((vh * w) / vw));
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob || !socket.connected || paused || !publishing) return;
          blob.arrayBuffer().then((ab) => socket.emit('camera:frame', ab));
        },
        'image/jpeg',
        0.72
      );
    };

    const id = setInterval(tick, FRAME_MS);
    return () => clearInterval(id);
  }, [publishing, paused, socketReady]);

  useEffect(() => {
    return () => {
      socketRef.current?.emit('camera:publisher-unregister');
      stopMedia();
    };
  }, []);

  const canStreamByPolicy = isAdmin || eligibility?.eligible === true;
  const blockedReason = !isAdmin && eligibility && eligibility.eligible === false ? eligibility.message : null;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        My camera
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Stream your webcam so authorized managers can verify presence. You choose when to allow video; pause
        stops sending frames to the server.
      </Typography>

      {loadingEligibility ? (
        <CircularProgress size={28} sx={{ mb: 2 }} />
      ) : blockedReason ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {blockedReason}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Use <strong>Allow video streaming</strong> to review consent, then we connect to the server and your
          browser will ask for the camera. Use <strong>Refresh connection</strong> if the server status stays
          disconnected.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview
            </Typography>
            <Box
              sx={{
                position: 'relative',
                bgcolor: 'grey.900',
                borderRadius: 1,
                overflow: 'hidden',
                aspectRatio: '4/3',
                maxWidth: 640,
              }}
            >
              <video
                ref={videoRef}
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }} alignItems="center">
              <Chip
                label={
                  socketReady
                    ? 'Live server connected'
                    : publishing
                      ? 'Live server reconnecting…'
                      : 'Live server not connected'
                }
                color={socketReady ? 'success' : 'warning'}
                size="small"
                variant={socketReady ? 'filled' : 'outlined'}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshSocketConnection}
                disabled={publishing}
              >
                Refresh connection
              </Button>
              <Chip
                label={publishing ? (paused ? 'Paused' : 'Streaming') : 'Off'}
                color={publishing ? (paused ? 'warning' : 'primary') : 'default'}
                size="small"
              />
            </Stack>
            {!socketReady && !publishing && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Open the permission dialog to connect to the server first; the browser camera prompt comes after
                the server is reachable. Try <strong>Refresh connection</strong> if needed.
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
              {!publishing ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={starting ? <CircularProgress size={18} color="inherit" /> : <CamOnIcon />}
                  onClick={openPermissionDialog}
                  disabled={!canStreamByPolicy || starting || loadingEligibility}
                >
                  Allow video streaming
                </Button>
              ) : (
                <>
                  <Button variant="outlined" color="error" startIcon={<CamOffIcon />} onClick={stopStream}>
                    Stop
                  </Button>
                  {!paused ? (
                    <Button variant="outlined" startIcon={<PauseIcon />} onClick={doPause}>
                      Pause feed
                    </Button>
                  ) : (
                    <Button variant="contained" startIcon={<ResumeIcon />} onClick={doResume}>
                      Resume feed
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 280 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Today (your time)
            </Typography>
            {loadingSummary ? (
              <CircularProgress size={28} />
            ) : summary ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Live video (approx.)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {summary.streaming_hours ?? 0} h
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Paused (feed off)
                </Typography>
                <Typography variant="h6">{summary.paused_hours ?? 0} h</Typography>
                <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                  {(summary.events ?? 0)} events logged today
                </Typography>
                <Button size="small" onClick={loadSummary} sx={{ mt: 1 }}>
                  Refresh
                </Button>
              </>
            ) : (
              <Typography variant="body2">No data yet.</Typography>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={permissionOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && (starting || connectingServer)) return;
          if (!starting && !connectingServer) setPermissionOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Allow video streaming?</DialogTitle>
        <DialogContent dividers>{PERMISSION_DISCLAIMER}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => {
              if (starting || connectingServer) {
                stopMedia();
                setStarting(false);
                setConnectingServer(false);
              }
              setPermissionOpen(false);
            }}
            color="inherit"
          >
            {starting || connectingServer ? 'Cancel' : 'Deny'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={runAllowStreaming}
            disabled={starting || connectingServer}
            startIcon={
              starting || connectingServer ? <CircularProgress size={18} color="inherit" /> : <CamOnIcon />
            }
          >
            {connectingServer ? 'Connecting to server…' : starting ? 'Opening camera…' : 'Allow'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeCamera;
